import { PaginationParams } from '../types/URLParamTypes'
import { ReadableSearchParams, toSearchParams, updateQueryParamsInPath } from '../URLParam/QueryParamPathHelper'

export const PAGE_QUERY_PARAM = 'page'
export const PER_PAGE_QUERY_PARAM = 'perPage'

export const DEFAULT_PAGE = 1
export const DEFAULT_PER_PAGE = 25

export interface PaginationQueryParams {
    page: number
    perPage: number
}

const parsePositiveInteger = (value: string | null, fallback: number): number => {
    if (value === null || value.trim() === '') return fallback
    const parsed = Number(value)
    if (!Number.isInteger(parsed) || parsed < 1) return fallback
    return parsed
}

/**
 * Reads the pagination state from URL search params. Values that are missing, non-numeric
 * or lower than 1 fall back to the provided defaults.
 */
export const extractQueryParamsForPagination = (
    source: ReadableSearchParams | string,
    defaults: Partial<PaginationQueryParams> = {}
): PaginationQueryParams => {
    const { page = DEFAULT_PAGE, perPage = DEFAULT_PER_PAGE } = defaults
    const searchParams = toSearchParams(source)

    return {
        page: parsePositiveInteger(searchParams.get(PAGE_QUERY_PARAM), page),
        perPage: parsePositiveInteger(searchParams.get(PER_PAGE_QUERY_PARAM), perPage),
    }
}

/**
 * Returns the path with the pagination state applied to its query string. Works with relative
 * paths, keeps any unrelated params (search, filters, active-tab) intact and omits params that
 * equal their default so the URL stays clean on the first page. Omitted values are left as they
 * are, which allows partial updates.
 */
export const addPaginationQueryParamsToPath = (
    path: string,
    pagination: Partial<PaginationQueryParams>,
    defaults: Partial<PaginationQueryParams> = {}
): string => {
    const { page = DEFAULT_PAGE, perPage = DEFAULT_PER_PAGE } = defaults

    return updateQueryParamsInPath(path, (params) => {
        const applyParam = (key: string, value: number | undefined, defaultValue: number): void => {
            if (value === undefined) return
            if (value === defaultValue) {
                params.delete(key)
                return
            }
            params.set(key, value.toString())
        }

        applyParam(PAGE_QUERY_PARAM, pagination.page, page)
        applyParam(PER_PAGE_QUERY_PARAM, pagination.perPage, perPage)
    })
}

/** Converts the page based state used in the URL to the offset based state used by the API. */
export const toPaginationParams = ({ page, perPage }: PaginationQueryParams): PaginationParams => ({
    offset: (page - 1) * perPage,
    limit: perPage,
})

/** Converts the offset based state used by the API to the page based state used in the URL. */
export const toPaginationQueryParams = ({ offset, limit }: PaginationParams): PaginationQueryParams => ({
    page: limit > 0 ? Math.floor(offset / limit) + 1 : DEFAULT_PAGE,
    perPage: limit > 0 ? limit : DEFAULT_PER_PAGE,
})
